import { newReferral } from "../patients/patients";
import { foldRecord } from "../../ui/fold";
import type { InboxItemView } from "../../../server/core/inbox";
import type { ScreenContext } from "../../shell/types";
import { composer, lastFromUs, threadView, typing, type Mode } from "../../ui/conversation";
import { live, publishCounts } from "../../shell/live";
import { api, el, fileName, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib } from "../../ui/record";
import { sendButton } from "../../ui/send";
import { openViewer } from "../../ui/viewer";
import { refuse } from "../clients/forms";
import { inboxChanged } from "./inbox";

// One inbox conversation (brief 1B-5, mockup v20 inboxRec): the bar with its label and business,
// Done, Next and the keys (J next, E done, R reply, N note); the thread, where each message shows
// who on the team sent it, from which line and its status, with the team's yellow notes and the
// calls; the composer (Text, Email, Call or Note, From and To); and on the side the linked
// record's snapshot with Open, and Who's on it. With AI off, a person links it with Link to….

type View = InboxItemView;
type Res = { ok: boolean; status: number; body: View & { error?: string } };

const BIZ_ICON: Record<string, string> = {
  "Home Care": "heart",
  Staffing: "briefcase",
  Transportation: "location",
};

const SENT: Record<Mode, [string, string]> = {
  text: ["Sent", "By text"],
  email: ["Sent", "By email"],
  call: ["Calling…", "Logged on this conversation"],
  note: ["Note added", "Only the team sees it"],
};

export async function itemScreen(host: HTMLElement, ctx: ScreenContext, id: string) {
  const page = el("div", { class: "record" });
  // Brief 1E-32: every card on the item folds.
  foldRecord(page, "inbox-item");
  host.append(page);
  const screen = ctx.screen.id;
  const first = (await api<View>(
    "GET",
    `/api/inbox/items/${encodeURIComponent(id)}?screen=${encodeURIComponent(screen)}`,
  )) as Res;
  if (!first.ok) {
    page.append(
      el(
        "div",
        { class: "blank", role: "alert" },
        icon("circle-slash", "big"),
        el("h2", {}, "Not available"),
        el(
          "p",
          { class: "muted" },
          "This message isn't here, or it's about a record this role can't open.",
        ),
      ),
    );
    return;
  }
  let view = first.body;
  let mode: Mode = view.mode as Mode;
  inboxChanged();

  const send = async (method: string, path: string, body?: unknown): Promise<Res> => {
    const res = (await api<View>(method, `/api/inbox/items/${id}${path}`, body)) as Res;
    if (res.ok) {
      view = { ...res.body, next: res.body.next ?? view.next };
      if (page.isConnected) draw();
      inboxChanged();
    }
    return res;
  };

  /** After Done (or Send and done): the next open item in this list, else the list. */
  const onward = () => ctx.go(view.next ? `/inbox/${screen}/${view.next}` : `/inbox/${screen}`);

  function linkTo() {
    const input = el("input", {
      type: "search",
      id: "linkq",
      placeholder: "Name, email or phone",
      autocomplete: "off",
    });
    const results = el("div", { class: "picks", role: "list", "aria-label": "Records found" });
    const m = openModal({
      title: view.record ? "Change the record" : "Link to…",
      sub: "Pick the client or person this is about.",
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el("div", { class: "field" }, el("label", { for: "linkq" }, "Search records"), input),
          results,
        ),
      ],
    });
    input.focus();
    let n = 0;
    input.addEventListener("input", async () => {
      const mine = ++n;
      const q = input.value.trim();
      if (q.length < 2) return results.replaceChildren();
      const res = await api<{
        records: { kind: string; id: string; name: string; line: string }[];
      }>("GET", `/api/inbox/records?q=${encodeURIComponent(q)}`);
      if (mine !== n) return;
      const found = res.ok ? res.body.records : [];
      results.replaceChildren(
        ...(found.length
          ? found.map((r) => {
              const b = el(
                "button",
                { type: "button", class: "btn", role: "listitem" },
                icon(r.kind === "client" ? "organization" : "person"),
                " ",
                el("b", {}, r.name),
                el("span", { class: "small muted" }, ` · ${r.line}`),
              );
              b.addEventListener("click", async () => {
                b.disabled = true;
                const done = await send("POST", "/link", { kind: r.kind, id: r.id });
                if (!done.ok) return refuse(m.note, done.body.error);
                m.close();
                toast(`Linked to ${r.name}`, "The conversation is on their record");
              });
              return b;
            })
          : [el("p", { class: "small muted" }, "No client or person matches.")]),
      );
    });
  }

  async function markDone() {
    if (view.item.status !== "open" || !view.canEdit) return;
    const res = await send("POST", "/done", {});
    if (!res.ok) return toast("That wasn't saved", res.body.error);
    toast("Marked done", "Moved to Done");
    onward();
  }

  let box: ReturnType<typeof composer> | null = null;
  // What a live update redraws in place (brief 1B-6).
  const threadSlot = el("div");
  const sideSlot = el("div", { class: "cvside" });
  let drawnOpen = view.item.status === "open";
  // A file opens in the viewer (brief 1B-9); after it's filed, the conversation shows where.
  const reload = async () => {
    const res = (await api<View>(
      "GET",
      `/api/inbox/items/${encodeURIComponent(id)}?screen=${encodeURIComponent(screen)}`,
    )) as Res;
    if (!res.ok || !page.isConnected) return;
    view = { ...res.body, next: res.body.next ?? view.next };
    draw();
  };
  const view1 = (a: { id: string; name: string }) => openViewer({ file: a, onDone: reload });
  // Brief 1E-32: newest first, earlier ones folded until "Show n earlier".
  const fold = { all: false };
  const threadNow = () =>
    threadView(
      view.entries,
      (a) => {
        const b = el(
          "button",
          { type: "button", class: "chipbtn" },
          icon("paperclip"),
          fileName(a.name),
        );
        b.setAttribute("aria-label", `View ${a.name}`);
        b.addEventListener("click", () => void view1(a));
        return el("span", { class: "filerow" }, b, sendButton({ files: [a] }));
      },
      fold,
    );

  /** The Attachments card (mockup v23): each file with an eye to view it, and where it's filed. */
  function attachmentsCard(): HTMLElement | null {
    const list = view.attachments;
    if (!list.length) return null;
    return el(
      "section",
      { class: "card", "aria-label": "Attachments" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("paperclip"), ` Attachments · ${list.length}`),
          el(
            "span",
            { class: "small muted" },
            "They open here. Nothing downloads to the computer unless the role allows it.",
          ),
        ),
      ),
      el(
        "div",
        { class: "attcard" },
        ...list.map((a) =>
          el(
            "span",
            { class: "attchip" },
            icon("file"),
            fileName(a.name),
            ib("eye", `View ${a.name}`, () => void view1(a)),
          ),
        ),
      ),
      ...list.flatMap((a) =>
        a.filed.map((line) =>
          el(
            "p",
            { class: "small muted", style: "margin:6px 0 0" },
            icon("check-circle"),
            ` ${line}`,
          ),
        ),
      ),
    );
  }

  // The clearing keys (shown on the bar), off while typing. Removed when the screen goes.
  const onKey = (e: KeyboardEvent) => {
    if (!page.isConnected) return document.removeEventListener("keydown", onKey);
    if (typing(e)) return;
    const k = e.key.toLowerCase();
    if (k === "j" && view.next) {
      e.preventDefault();
      ctx.go(`/inbox/${screen}/${view.next}`);
    } else if (k === "e") {
      e.preventDefault();
      void markDone();
    } else if ((k === "r" || k === "n") && box) {
      e.preventDefault();
      box.focus(k === "n" ? "note" : mode === "note" ? (view.mode as Mode) : mode);
    }
  };
  document.addEventListener("keydown", onKey);

  /** Start intake (brief 1C-1, the mockup's action on a referral email). */
  function startIntakeButton(): HTMLElement | null {
    const si = view.startIntake;
    if (!si || !view.canEdit) return null;
    const b = el(
      "button",
      { type: "button", class: "btn sm pri" },
      icon("person-add"),
      " Start intake",
    );
    b.addEventListener(
      "click",
      () =>
        void newReferral(ctx, {
          source: "inbox",
          inboxItemId: view.item.id,
          clientId: si.clientId,
          hint: `From ${view.item.from}. The conversation and its attachments move onto the new patient.`,
          done: () => inboxChanged(),
        }),
    );
    return b;
  }

  function snapshotCard(): HTMLElement {
    const linkBtn = (label: string, pri: boolean) => {
      const b = el(
        "button",
        { type: "button", class: pri ? "btn sm pri" : "btn sm ghost" },
        icon("link"),
        ` ${label}`,
      );
      b.addEventListener("click", linkTo);
      return b;
    };
    const s = view.snapshot;
    if (!view.record)
      return el(
        "section",
        { class: "card" },
        el("h2", {}, icon("link"), " Not linked yet"),
        el(
          "p",
          { class: "small muted" },
          "Link this conversation to a record and its snapshot shows here.",
        ),
        el(
          "div",
          { class: "actions" },
          startIntakeButton(),
          view.canEdit && view.item.status !== "done"
            ? linkBtn("Link to…", !view.startIntake)
            : null,
        ),
      );
    const open = view.record.path
      ? (() => {
          const path = view.record.path;
          const b = el(
            "button",
            { type: "button", class: "btn sm" },
            icon("link-external"),
            " Open",
          );
          b.setAttribute("aria-label", `Open ${view.record.name}`);
          b.addEventListener("click", () => ctx.go(path));
          return b;
        })()
      : null;
    return el(
      "section",
      { class: "card", "aria-label": "Snapshot" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("div", { class: "eyebrow" }, s?.eyebrow ?? ""),
          el("h2", {}, view.record.name),
        ),
        open,
      ),
      s
        ? el(
            "dl",
            { class: "kv" },
            ...s.rows.flatMap((r) => [
              el("dt", {}, r.label),
              el("dd", {}, r.level ? chip(r.value, r.level) : r.value),
            ]),
          )
        : el("p", { class: "small muted" }, "Its module is off, so there's nothing to show."),
      el(
        "p",
        { class: "small muted", style: "margin:8px 0 0" },
        "A snapshot. Open the full record for the rest.",
      ),
      el(
        "div",
        { class: "actions" },
        startIntakeButton(),
        view.canEdit && view.item.status === "open" ? linkBtn("Change the record", false) : null,
      ),
    );
  }

  function draw() {
    const it = view.item;
    ctx.crumb(it.from);
    const open = it.status === "open";
    const head = el(
      "div",
      { class: "head" },
      el(
        "div",
        { class: "t" },
        el("div", { class: "eyebrow" }, it.label),
        el("h1", {}, it.subject || "(no subject)"),
        el(
          "div",
          { class: "muted" },
          [it.from, `${it.sourceLabel}${it.to ? ` to ${it.to}` : ""}`, it.when].join(" · "),
        ),
      ),
    );

    // The bar: what it is, then Done, Next and the keys.
    const acts: HTMLElement[] = [];
    if (open && view.canEdit) {
      const done = el("button", { type: "button", class: "btn sm" }, icon("check"), " Done");
      done.addEventListener("click", () => void markDone());
      acts.push(done);
    }
    if (view.canEdit && it.status === "filtered") {
      const bring = el(
        "button",
        { type: "button", class: "btn sm pri" },
        icon("inbox"),
        " Bring it in",
      );
      bring.addEventListener("click", async () => {
        const res = await send("POST", "/bring-in");
        if (!res.ok) return toast("That wasn't saved", res.body.error);
        toast("Brought in", "It's in Needs action");
      });
      acts.push(bring);
    }
    // A claim, time off or availability change (brief 1D-3): the office approves or declines.
    const dec = view.decide;
    if (dec && dec.open && view.canDecide) {
      const post = async (path: string, approve: boolean, note: string) => {
        const res = await api<{ error?: string }>("POST", path, { note });
        if (!res.ok) return res.body.error ?? "That wasn't saved.";
        toast(approve ? "Approved" : "Declined", "The caregiver gets a text");
        inboxChanged();
        await reload();
        return null;
      };
      const answer = (path: string, approve: boolean) => {
        if (approve)
          return void post(path, true, "").then((e) => e && toast("That wasn't saved", e));
        const input = el("textarea", { id: "dec-why", maxlength: "300" });
        const go = el("button", { type: "button", class: "btn pri" }, icon("x-circle"), " Decline");
        const m = openModal({
          title: "Decline",
          sub: dec.needsWhy
            ? "Say why. The caregiver gets it in a text (no patient details)."
            : "A note for the caregiver (optional). They get it in a text.",
          body: [
            el(
              "div",
              { class: "field" },
              el("label", { for: "dec-why" }, dec.needsWhy ? "Why" : "Note (optional)"),
              input,
            ),
          ],
          actions: [go],
        });
        input.focus();
        go.addEventListener("click", async () => {
          if (dec.needsWhy && !input.value.trim()) return refuse(m.note, "Say why.");
          go.disabled = true;
          const e = await post(path, false, input.value.trim());
          if (e) {
            go.disabled = false;
            return refuse(m.note, e);
          }
          m.close();
        });
      };
      const yes = el("button", { type: "button", class: "btn sm pri" }, icon("check"), " Approve");
      yes.addEventListener("click", () => void answer(dec.approve, true));
      const no = el("button", { type: "button", class: "btn sm" }, icon("x-circle"), " Decline");
      no.addEventListener("click", () => void answer(dec.decline, false));
      acts.unshift(yes, no);
    } else if (dec && !dec.open)
      acts.unshift(
        chip(
          dec.status === "approved" ? "Approved" : "Declined",
          dec.status === "approved" ? "ok" : "plain",
        ),
      );
    // A Missed call: Call back from its line, through the connected provider (brief 1B-6).
    const back = view.callBack;
    if (open && view.canEdit && back) {
      const b = el(
        "button",
        { type: "button", class: "btn sm pri" },
        icon("device-mobile"),
        " Call back",
      );
      b.addEventListener("click", async () => {
        b.disabled = true;
        const res = await send("POST", "/send", {
          mode: "call",
          from: back.from,
          to: back.to,
          body: "",
          keepOpen: true,
          screen,
        });
        b.disabled = false;
        if (!res.ok) return toast("The call didn't start", res.body.error);
        toast("Calling…", "The phone on the profile rings first, then the call connects");
      });
      acts.unshift(b);
    }
    const next = el(
      "button",
      { type: "button", class: "btn sm", disabled: !view.next },
      "Next ",
      icon("arrow-right"),
    );
    next.addEventListener("click", () => {
      if (view.next) ctx.go(`/inbox/${screen}/${view.next}`);
    });
    acts.push(
      next,
      el("span", { class: "small muted keys" }, "Keys: J next · E done · R reply · N note"),
    );
    const bar = el(
      "div",
      { class: "actions cvbar" },
      el(
        "span",
        { class: "small muted" },
        chip(it.label, it.level),
        it.business
          ? el(
              "span",
              { class: "bizt" },
              icon(BIZ_ICON[it.business] ?? "organization"),
              ` ${it.business}`,
            )
          : null,
      ),
      el("span", { class: "actions" }, ...acts),
    );

    threadSlot.replaceChildren(threadNow());
    box = null;
    let below: HTMLElement;
    if (open && view.canEdit) {
      box = composer({
        compose: view.compose,
        mode,
        to: it.fromAddr || null,
        inbox: true,
        aiItem: it.id,
        last: lastFromUs(view.entries),
        onMode: (m) => (mode = m),
        send: async (b) => {
          const res = await send("POST", "/send", { ...b, screen });
          if (!res.ok) return res.body.error ?? "That wasn't sent.";
          const [what, sub] = SENT[b.mode];
          const mentioned =
            (res.body as View & { sent?: { mentioned: number } }).sent?.mentioned ?? 0;
          toast(
            what,
            b.mode === "note" && mentioned
              ? `They'll get a notification`
              : (b.mode === "text" || b.mode === "email") && !b.keepOpen
                ? "Moved to Done"
                : b.mode === "text" || b.mode === "email"
                  ? "Still open"
                  : sub,
          );
          if (res.body.item.status === "done") onward();
          return null;
        },
      });
      below = box.el;
    } else
      below = el(
        "p",
        {},
        chip(it.status === "done" ? "Done" : "Kept out", it.status === "done" ? "ok" : "plain"),
        " ",
        it.doneNote ?? "",
      );

    // Who's on it.
    const who = el(
      "select",
      { id: "cva", disabled: !view.canEdit },
      el("option", { value: "" }, "Unassigned"),
      ...view.users.map((u) =>
        el("option", { value: u.id, selected: u.id === it.assignee }, u.name),
      ),
    );
    who.addEventListener("change", async () => {
      const res = await send("PUT", "", { assignee: who.value || null });
      if (!res.ok) return toast("That wasn't saved", res.body.error);
      const name = view.users.find((u) => u.id === who.value)?.name;
      toast(name ? `Assigned to ${name}` : "Unassigned", "Everyone sees who has it");
    });
    const whoCard = el(
      "section",
      { class: "card" },
      el("h2", {}, icon("people"), " Who's on it"),
      el("div", { class: "field" }, el("label", { for: "cva" }, "Assigned to"), who),
      el(
        "p",
        { class: "small muted", style: "margin:6px 0 0" },
        "An @mention in a note also tells that person.",
      ),
    );

    sideSlot.replaceChildren(snapshotCard(), whoCard);
    drawnOpen = open;
    page.replaceChildren(
      head,
      attachmentsCard() ?? "",
      bar,
      reviewCard() ?? "",
      // Brief 1E-32: the reply box first, then the thread, newest first.
      el("div", { class: "cvgrid" }, el("section", { class: "card" }, below, threadSlot), sideSlot),
    );
  }
  /**
   * The access review (brief 1E-7): each person's jobs and extras, Confirm or Remove per line,
   * then Mark reviewed. Admin only (the server sends it only to an Admin).
   */
  function reviewCard(): HTMLElement | null {
    const r = view.review;
    if (!r) return null;
    const decide = async (l: (typeof r.lines)[number], decision: "confirm" | "remove") => {
      const res = await api<{ error?: string }>("POST", `/api/access-reviews/${r.id}/decide`, {
        userId: l.userId,
        line: l.line,
        decision,
      });
      if (!res.ok) return toast("That wasn't saved", res.body.error);
      toast(
        decision === "confirm" ? "Confirmed" : "Removed",
        decision === "confirm" ? `${l.person}: ${l.what}` : `Taken off ${l.person}`,
      );
      await reload();
    };
    const rows = r.lines.map((l) =>
      el(
        "tr",
        { "data-review-line": `${l.person} · ${l.what}` },
        el("td", {}, el("b", {}, l.person)),
        el("td", {}, l.what),
        el(
          "td",
          { class: "num" },
          l.decision
            ? chip(
                l.decision === "confirm" ? "Confirmed" : "Removed",
                l.decision === "confirm" ? "ok" : "plain",
              )
            : r.reviewed
              ? ""
              : el(
                  "span",
                  { class: "actions" },
                  (() => {
                    const b = el(
                      "button",
                      { type: "button", class: "btn sm" },
                      icon("check"),
                      " Confirm",
                    );
                    b.setAttribute("aria-label", `Confirm ${l.what} for ${l.person}`);
                    b.addEventListener("click", () => void decide(l, "confirm"));
                    return b;
                  })(),
                  (() => {
                    const b = el(
                      "button",
                      { type: "button", class: "btn sm" },
                      icon("x"),
                      " Remove",
                    );
                    b.setAttribute("aria-label", `Remove ${l.what} from ${l.person}`);
                    b.addEventListener("click", () => void decide(l, "remove"));
                    return b;
                  })(),
                ),
        ),
      ),
    );
    const done = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("check-circle"),
      " Mark reviewed",
    );
    done.addEventListener("click", async () => {
      done.disabled = true;
      const res = await api<{ error?: string }>("POST", `/api/access-reviews/${r.id}/reviewed`);
      done.disabled = false;
      if (!res.ok) return toast("That wasn't saved", res.body.error);
      toast("Access review done", "Lines left as they were are recorded as confirmed");
      inboxChanged();
      await reload();
    });
    return el(
      "section",
      { class: "card", "aria-label": "Access review" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("shield-check"), " Access review"),
          el(
            "span",
            { class: "small muted" },
            "Confirm each person's jobs and extras, or remove what's no longer needed.",
          ),
        ),
        r.reviewed ? chip("Reviewed", "ok") : done,
      ),
      el(
        "div",
        { class: "tw" },
        el(
          "table",
          {},
          el("caption", { class: "sr" }, "Access review"),
          el(
            "thead",
            {},
            el(
              "tr",
              {},
              el("th", { scope: "col" }, "Person"),
              el("th", { scope: "col" }, "Job or extras"),
              el("th", { scope: "col", class: "num" }, ""),
            ),
          ),
          el("tbody", {}, ...rows),
        ),
      ),
    );
  }

  draw();

  // Live (brief 1B-6): every 15 seconds while the tab is visible, the conversation again only when
  // it changed. New messages and calls show without touching what's being typed in the composer.
  live.watch("inbox", async () => {
    if (!page.isConnected) return false;
    const got = await api<{ counts: unknown; item?: View }>(
      "GET",
      `/api/inbox/changes?item=${encodeURIComponent(id)}&screen=${encodeURIComponent(screen)}&stamp=${encodeURIComponent(view.stamp)}`,
      undefined,
      { background: true },
    );
    if (!got.ok || !page.isConnected) return page.isConnected;
    publishCounts(got.body.counts);
    const fresh = got.body.item;
    if (!fresh) return true;
    view = { ...fresh, next: fresh.next ?? view.next };
    // Done (or kept out) elsewhere: the whole screen follows. Otherwise only the thread is redrawn.
    if ((view.item.status === "open") !== drawnOpen) draw();
    else {
      threadSlot.replaceChildren(threadNow());
      ctx.crumb(view.item.from);
    }
    return true;
  });
}
